import { SearchOutlined } from '@ant-design/icons';
import { Input, Form, Flex } from 'antd';
import { useCallback, useState, type FC } from 'react';

import { MultipleSelect, SearchButtons } from '@app/components';
import { PREFIX } from '@app/constants';
import { useOrganizationUsersFiltersEffect } from '@app/pages/Organizations/OrganizationUsers/hooks/useOrganizationUsersFiltersEffects';
import type {
  FiltersFormValues,
  OrganizationUsersFiltersProps,
} from '@app/pages/Organizations/OrganizationUsers/OrganizationUsers.types';

import type { BaseOptionType } from 'antd/es/select';

const { useForm } = Form;

const STATUSES_SELECTOR_OPTIONS = [
  {
    label: 'Владелец',
    value: 'admin',
  },
  {
    label: 'Пользователь',
    value: 'user',
  },
  {
    label: 'В архиве',
    value: 'deleted',
  },
];

export const OrganizationUsersFilters: FC<OrganizationUsersFiltersProps> = ({
  setRequestData,
}) => {
  const [selectedPositions, setSelectedPositions] = useState<BaseOptionType[]>(
    []
  );
  const [selectedBranches, setSelectedBranches] = useState<BaseOptionType[]>(
    []
  );
  const [selectedStatuses, setSelectedStatuses] = useState<BaseOptionType[]>(
    []
  );

  const [form] = useForm();

  const { allBranches, allPositions } = useOrganizationUsersFiltersEffect();

  const handleSubmitForm = useCallback(
    ({ SearchTerm }: FiltersFormValues) => {
      setRequestData((prev) => {
        return {
          ...prev,
          SearchTerm,
          Branches: selectedBranches.map(({ value }) => value),
          Positions: selectedPositions.map(({ value }) => value),
          Statuses: selectedStatuses.map(({ value }) => value),
        };
      });
    },
    [selectedBranches, selectedPositions, selectedStatuses, setRequestData]
  );

  const handleReset = useCallback(() => {
    form.resetFields();
    setSelectedPositions([]);
    setSelectedBranches([]);
    setSelectedStatuses([]);
    setRequestData((prev) => ({
      ...prev,
      SearchTerm: '',
      Branches: [],
      Positions: [],
      Statuses: [],
    }));
  }, [form, setRequestData]);

  return (
    <div className={`${PREFIX}organization-users-filters`}>
      <Form
        form={form}
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 30 }}
        onFinish={handleSubmitForm}
      >
        <div className="toolbar-space">
          <Form.Item name="SearchTerm" wrapperCol={{ span: 16 }}>
            <Input
              placeholder="Поиск (по имени, телефону, email)"
              prefix={<SearchOutlined />}
              data-testid="search-users"
            />
          </Form.Item>
          <Flex justify="space-between" wrap gap={16}>
            <Flex gap={16} wrap>
              <MultipleSelect
                className="user-selector branch-selector"
                placeholder="Все филиалы"
                allOptions={
                  allBranches?.Branches?.map(({ Name, Id }) => {
                    return {
                      label: Name,
                      value: Id,
                    };
                  }) || []
                }
                width={100}
                selectedOption={selectedBranches}
                setSelectedOptions={setSelectedBranches}
                placeHolderDataTestId="all-branches"
                dropdownDataTestId="branch-dropdown"
                optionDataTestId="branch-item"
              />
              <MultipleSelect
                className="user-selector position-selector"
                placeholder="Все должности"
                allOptions={
                  allPositions?.map(({ Name, Id }) => {
                    return {
                      label: Name,
                      value: Id,
                    };
                  }) || []
                }
                width={100}
                selectedOption={selectedPositions}
                setSelectedOptions={setSelectedPositions}
                placeHolderDataTestId="all-positions"
                dropdownDataTestId="position-dropdown"
                optionDataTestId="position-item"
              />
              <MultipleSelect
                className="user-selector status-selector"
                placeholder="Все статусы"
                allOptions={STATUSES_SELECTOR_OPTIONS}
                width={100}
                selectedOption={selectedStatuses}
                setSelectedOptions={setSelectedStatuses}
                placeHolderDataTestId="all-statuses"
                dropdownDataTestId="status-dropdown"
                optionDataTestId="status-item"
              />
            </Flex>
            <SearchButtons
              className="tablet-buttons"
              resetAll={handleReset}
              showData={form.submit}
            />
          </Flex>
        </div>
      </Form>
    </div>
  );
};
